import {
  GATEWAY_FATAL_CLOSE_CODES,
  GATEWAY_VERSION,
  GatewayCloseCodes,
  GatewayOpcodes,
  type GatewayHello,
  type GatewayIdentify,
  type GatewayPayload,
  type GatewayPresenceUpdate,
} from '../contracts/chat-gateway.js';

export type GatewayStatus =
  /** Sem conexão e sem tentar (antes de `connect` ou depois de `disconnect`). */
  | 'disconnected'
  | 'connecting'
  | 'identifying'
  | 'resuming'
  | 'ready'
  /** Caiu; vai tentar de novo (`retryInMs`). */
  | 'reconnecting'
  /** Fechou de vez: login recusado (4004) ou versão errada (4012). */
  | 'closed';

export type GatewayStatusInfo = {
  status: GatewayStatus;
  /** Próxima tentativa, quando `reconnecting`. */
  retryInMs?: number;
  /** Código de fechamento que levou ao estado. */
  closeCode?: number;
};

/** O mínimo de WebSocket que o cliente usa (navegador, React Native, Node 22+). */
export interface GatewaySocket {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code: number; reason?: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export type GatewaySocketFactory = (url: string) => GatewaySocket;

export type GatewayTimers = {
  setTimeout(handler: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
};

export type GatewayOptions = {
  /** URL do `GET /chat/gateway` (já com `?v=10&encoding=json`). */
  url: string;
  /** Celular: o access token vai no Identify/Resume. Navegador: cookie (sem token). */
  token?: () => string | null | undefined | Promise<string | null | undefined>;
  properties: GatewayIdentify['properties'];
  presence?: GatewayPresenceUpdate;
  socket?: GatewaySocketFactory;
  onDispatch: (t: string, d: unknown, s: number) => void;
  onStatus?: (info: GatewayStatusInfo) => void;
  /** Fechou de vez (4004 = sessão de login inválida → tela de entrar). */
  onFatal?: (code: number, reason: string) => void;
  timers?: GatewayTimers;
  random?: () => number;
  /** Esperas entre tentativas (ms), com variação; a última se repete. */
  backoff?: number[];
};

const OPEN = 1;
/** Fechar mantendo a sessão retomável (o 1000 diria ao servidor que acabou). */
const RESUMABLE_CLOSE = 4000;
const DEFAULT_BACKOFF = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000];

/**
 * Cliente do gateway no protocolo do Discord: Hello → Identify (ou Resume,
 * se tem sessão), heartbeat com a primeira batida em instante aleatório,
 * conexão "zumbi" (heartbeat sem resposta) derrubada e retomada, Reconnect e
 * Invalid Session do servidor, reconexão com espera crescente e parada
 * definitiva nos códigos 4004 e 4012. Os eventos saem por `onDispatch`, em
 * ordem; quem guarda o estado são as stores.
 */
export class GatewayClient {
  private socket: GatewaySocket | null = null;
  private sessionId: string | null = null;
  private resumeUrl: string | null = null;
  private seq: number | null = null;
  private heartbeatInterval = 0;
  private heartbeatTimer: unknown = null;
  private reconnectTimer: unknown = null;
  private identifyTimer: unknown = null;
  private acked = true;
  private attempts = 0;
  private wanted = false;
  private currentStatus: GatewayStatus = 'disconnected';
  private readonly timers: GatewayTimers;
  private readonly random: () => number;

  constructor(private readonly options: GatewayOptions) {
    this.timers = options.timers ?? {
      setTimeout: (handler, ms) => setTimeout(handler, ms),
      clearTimeout: (handle) =>
        clearTimeout(handle as ReturnType<typeof setTimeout>),
    };
    this.random = options.random ?? Math.random;
  }

  get status(): GatewayStatus {
    return this.currentStatus;
  }

  /** Sessão atual (para depuração e testes). */
  get session(): { id: string | null; seq: number | null } {
    return { id: this.sessionId, seq: this.seq };
  }

  connect(): void {
    this.wanted = true;
    this.cancelReconnect();
    if (this.socket) return;
    this.open();
  }

  /** Fecha sem reconectar (sair, trocar de conta). A sessão acaba. */
  disconnect(): void {
    this.wanted = false;
    this.cancelReconnect();
    this.stopHeartbeat();
    this.clearIdentifyTimer();
    const socket = this.socket;
    this.socket = null;
    this.sessionId = null;
    this.resumeUrl = null;
    this.seq = null;
    if (socket) {
      detach(socket);
      try {
        socket.close(1000, 'Saindo');
      } catch {
        // já fechado
      }
    }
    this.setStatus({ status: 'disconnected' });
  }

  /** Reconecta já (botão "Tentar agora", rede voltou). */
  reconnectNow(): void {
    if (!this.wanted) return;
    this.cancelReconnect();
    if (this.socket) {
      this.dropSocket(RESUMABLE_CLOSE);
    }
    this.open();
  }

  /** `op 3`: muda o status (online, ausente, não perturbe, invisível). */
  updatePresence(presence: GatewayPresenceUpdate): void {
    this.options.presence = presence;
    if (this.currentStatus === 'ready') {
      this.send(GatewayOpcodes.PRESENCE_UPDATE, presence);
    }
  }

  // -------------------------------------------------------------------------

  private open(): void {
    const url = this.resumeUrl ?? this.options.url;
    this.setStatus({ status: 'connecting' });
    const factory =
      this.options.socket ??
      ((target: string) =>
        new (
          globalThis as unknown as {
            WebSocket: new (url: string) => GatewaySocket;
          }
        ).WebSocket(target));
    const socket = factory(url);
    this.socket = socket;
    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      void this.onMessage(event.data);
    };
    socket.onclose = (event) => {
      if (this.socket !== socket) return;
      this.onClose(event.code, event.reason ?? '');
    };
    socket.onerror = () => undefined;
    socket.onopen = () => undefined;
  }

  private async onMessage(data: unknown): Promise<void> {
    let payload: GatewayPayload;
    try {
      payload = JSON.parse(String(data)) as GatewayPayload;
    } catch {
      return;
    }
    switch (payload.op) {
      case GatewayOpcodes.HELLO:
        this.heartbeatInterval = (payload.d as GatewayHello).heartbeat_interval;
        this.startHeartbeat();
        if (this.sessionId && this.seq !== null) await this.resume();
        else await this.identify();
        return;
      case GatewayOpcodes.HEARTBEAT_ACK:
        this.acked = true;
        return;
      case GatewayOpcodes.HEARTBEAT:
        this.send(GatewayOpcodes.HEARTBEAT, this.seq);
        return;
      case GatewayOpcodes.RECONNECT:
        this.dropSocket(RESUMABLE_CLOSE);
        this.scheduleReconnect(0);
        return;
      case GatewayOpcodes.INVALID_SESSION: {
        const resumable = payload.d === true;
        if (!resumable) {
          this.sessionId = null;
          this.seq = null;
          this.resumeUrl = null;
        }
        // O Discord manda esperar de 1 a 5 s antes de identificar de novo.
        this.clearIdentifyTimer();
        this.identifyTimer = this.timers.setTimeout(
          () => {
            this.identifyTimer = null;
            void (resumable ? this.resume() : this.identify());
          },
          1_000 + Math.floor(this.random() * 4_000),
        );
        return;
      }
      case GatewayOpcodes.DISPATCH: {
        if (typeof payload.s === 'number') this.seq = payload.s;
        const t = payload.t ?? '';
        if (t === 'READY') {
          const ready = payload.d as {
            session_id: string;
            resume_gateway_url?: string;
          };
          this.sessionId = ready.session_id;
          this.resumeUrl = ready.resume_gateway_url
            ? withGatewayQuery(ready.resume_gateway_url)
            : null;
          this.attempts = 0;
          this.setStatus({ status: 'ready' });
        } else if (t === 'RESUMED') {
          this.attempts = 0;
          this.setStatus({ status: 'ready' });
        }
        this.options.onDispatch(t, payload.d, payload.s ?? 0);
        return;
      }
      default:
        return;
    }
  }

  private async identify(): Promise<void> {
    this.setStatus({ status: 'identifying' });
    const token = await this.options.token?.();
    const d: GatewayIdentify = {
      ...(token ? { token } : {}),
      properties: this.options.properties,
      ...(this.options.presence ? { presence: this.options.presence } : {}),
    };
    this.send(GatewayOpcodes.IDENTIFY, d);
  }

  private async resume(): Promise<void> {
    this.setStatus({ status: 'resuming' });
    const token = await this.options.token?.();
    this.send(GatewayOpcodes.RESUME, {
      ...(token ? { token } : {}),
      session_id: this.sessionId,
      seq: this.seq,
    });
  }

  private onClose(code: number, reason: string): void {
    this.socket = null;
    this.stopHeartbeat();
    this.clearIdentifyTimer();
    if (GATEWAY_FATAL_CLOSE_CODES.has(code)) {
      this.wanted = false;
      this.sessionId = null;
      this.seq = null;
      this.resumeUrl = null;
      this.setStatus({ status: 'closed', closeCode: code });
      this.options.onFatal?.(code, reason);
      return;
    }
    if (code === GatewayCloseCodes.INVALID_SEQ) {
      this.sessionId = null;
      this.seq = null;
      this.resumeUrl = null;
    }
    if (!this.wanted) {
      this.setStatus({ status: 'disconnected', closeCode: code });
      return;
    }
    this.scheduleReconnect(this.nextDelay(), code);
  }

  private scheduleReconnect(delay: number, closeCode?: number): void {
    this.cancelReconnect();
    this.setStatus({ status: 'reconnecting', retryInMs: delay, closeCode });
    this.reconnectTimer = this.timers.setTimeout(() => {
      this.reconnectTimer = null;
      if (this.wanted && !this.socket) this.open();
    }, delay);
  }

  private nextDelay(): number {
    const steps = this.options.backoff ?? DEFAULT_BACKOFF;
    const base = steps[Math.min(this.attempts, steps.length - 1)];
    this.attempts++;
    // ±20% para não voltarem todos juntos depois de uma queda do servidor.
    return Math.round(base * (0.8 + this.random() * 0.4));
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.acked = true;
    // A primeira batida num instante aleatório do intervalo, como pede o Discord.
    this.scheduleHeartbeat(Math.floor(this.heartbeatInterval * this.random()));
  }

  private scheduleHeartbeat(delay: number): void {
    this.heartbeatTimer = this.timers.setTimeout(() => {
      this.heartbeatTimer = null;
      if (!this.socket) return;
      if (!this.acked) {
        // Conexão zumbi: derruba e retoma.
        this.dropSocket(RESUMABLE_CLOSE);
        this.scheduleReconnect(0);
        return;
      }
      this.acked = false;
      this.send(GatewayOpcodes.HEARTBEAT, this.seq);
      this.scheduleHeartbeat(this.heartbeatInterval);
    }, delay);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      this.timers.clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearIdentifyTimer(): void {
    if (this.identifyTimer !== null) {
      this.timers.clearTimeout(this.identifyTimer);
      this.identifyTimer = null;
    }
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      this.timers.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Larga o socket atual sem esperar o `close` (a sessão segue retomável). */
  private dropSocket(code: number): void {
    const socket = this.socket;
    this.socket = null;
    this.stopHeartbeat();
    if (!socket) return;
    detach(socket);
    try {
      socket.close(code, 'Reconectando');
    } catch {
      // já fechado
    }
  }

  private send(op: number, d: unknown): void {
    if (this.socket?.readyState === OPEN) {
      this.socket.send(JSON.stringify({ op, d }));
    }
  }

  private setStatus(info: GatewayStatusInfo): void {
    this.currentStatus = info.status;
    this.options.onStatus?.(info);
  }
}

function detach(socket: GatewaySocket): void {
  socket.onopen = null;
  socket.onmessage = null;
  socket.onclose = null;
  socket.onerror = null;
}

/** A URL de retomada do READY vem sem a versão e a codificação. */
export function withGatewayQuery(url: string): string {
  const [base, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  if (!params.has('v')) params.set('v', String(GATEWAY_VERSION));
  if (!params.has('encoding')) params.set('encoding', 'json');
  return `${base}?${params.toString()}`;
}
